import { Component, ElementRef, inject, OnInit } from '@angular/core';
import { untilDestroyed } from '@ngneat/until-destroy';
import { TasksComposition } from '@shared/compositions';
import { ConfirmationDialogType, DeleteConfirmationOptions, SectionType, TaskType } from '@core/enums';
import { group } from '@angular/animations';
import { SubTaskEntity, TaskEntity } from '@core/entities';
import { DateTimeUtility, GetUserId, isMobileView } from '@core/utils';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { ConfirmationDialogComponent } from '@web/_shared/dialogs';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { DatePipe, NgStyle, TitleCasePipe } from '@angular/common';
import { TippyModule } from '@ngneat/helipopper';
import { DialogRef } from '@ngneat/dialog';
import { ButtonComponent, DropdownComponent, IconComponent, ImageSelectorComponent } from '@shared/components/ui';
import { DatePickerInputComponent, FormErrorComponent } from '@shared/components';
import { SubtaskFormComponent } from '@shared/components/forms';

@Component({
  selector: 'app-tasks-form',
  templateUrl: './tasks-form.component.html',
  styleUrls: ['./tasks-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormErrorComponent,
    TranslateModule,
    ImageSelectorComponent,
    DatePickerInputComponent,
    NgSelectModule,
    DropdownComponent,
    IconComponent,
    NgStyle,
    TippyModule,
    DatePipe,
    ButtonComponent,
    TitleCasePipe,
  ],
})
export class TasksFormComponent extends TasksComposition implements OnInit {
  task: TaskEntity;
  subtaskSheet: any;
  bottomSheetRef: any;
  dialogRef: DialogRef;
  groupId: number | null = null;
  protected readonly TasksType = TaskType;
  protected readonly group = group;
  protected readonly isMobileView = isMobileView;
  protected readonly SectionType = SectionType;
  private readonly _elRef = inject(ElementRef);

  constructor() {
    super();

    // this.task = this._bottomSheetService.currentSheetData.task;

    if (!isMobileView()) {
      this.dialogRef = inject(DialogRef);
      this.task = this.dialogRef.data?.task;
      if (this.dialogRef.data?.groupId) {
        this.groupId = Number(this.dialogRef.data.groupId);
      }
      if (this.task && this.task.id) {
        this._fetchTaskById(this.task.id);
      }
    }
  }

  get isUpdateMode(): boolean {
    return !!(this.task && this.task.id && this.task.imageUrl);
  }

  get taskId(): number {
    return this.task?.id || null;
  }

  get shouldLockGroupDropdown(): boolean {
    return !!this.groupId;
  }

  ngOnInit(): void {
    this.initForm();
    this.fetchDataForForm();
    if (isMobileView()) {
      this.task = this._bottomSheetService.currentSheetData.task;
      if (this._bottomSheetService.currentSheetData.groupId) {
        this.groupId = Number(this._bottomSheetService.currentSheetData.groupId);
      }
      if (this.task && this.task.id) {
        this._fetchTaskById(this.task.id);
      }
    }

    if (this.groupId && !this.isUpdateMode) {
      this.form.patchValue({ type: TaskType.Group });
      this.form.get('type')?.disable();

      const groupCheckInterval = setInterval(() => {
        if (this.groups && this.groups.length > 0) {
          clearInterval(groupCheckInterval);
          this.form.patchValue({ groupId: this.groupId });
          this.form.get('groupId')?.disable();
        }
      }, 100);
    }
  }

  openSubtaskBottomSheet(): void {
    if (this.form.get('groupId')?.invalid || this.form.get('collaborators')?.invalid) {
      this.form.get('groupId')?.markAsTouched();
      this.form.get('type')?.markAsTouched();
      this.form.get('collaborators')?.markAsTouched();
      return;
    }
    const groupParticipants = this.groups.find((group) => group.id === this.form.value.groupId)?.participants;
    if (this.form.value.type === TaskType.Group) {
      groupParticipants?.splice(
        groupParticipants.findIndex((user) => user.userId === +GetUserId()),
        1,
      );
    }

    if (!isMobileView()) {
      this.subtaskSheet = this._dialogService.open(SubtaskFormComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          taskType: this.form.value.type,
          teamMembers: this.users,
          groupParticipants,
          collaborators: this.form.value.collaborators,
        },
      });
    } else {
      this.subtaskSheet = this._bottomSheetService.open(SubtaskFormComponent, {
        height: '55%',
        showCloseButton: false,
        data: {
          taskType: this.form.value.type,
          teamMembers: this.users,
          groupParticipants,
          collaborators: this.form.value.collaborators,
        },
      });
    }
    this.subtaskSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          if (this.currentSubtasks && this.currentSubtasks.length) {
            this.currentSubtasks.push(result);
            // this.subTasksListOfATask = [...this.subTasksListOfATask, [...result]];
          } else {
            this.currentSubtasks = [result];
          }
          this.prepareSubtasks(this.currentSubtasks);
          this._patchSubTasks(this.currentSubtasks);
        }
      },
    });
  }

  // function to assign values from assignTo to assign_to for each subsTask
  prepareSubtasks(subtasks: any) {
    return subtasks.forEach((subtask: any) => {
      if (subtask.assignedTo && subtask.assignedTo.length) {
        subtask.assigned_to = subtask.assignedTo.map((user: any) => {
          return { user_id: user.userId, approved_status: 1 };
        });
        delete subtask.assignedTo;
      }

      subtask.assigned_to = subtask.assigned_to.map((user: any) => {
        return { user_id: user.user_id, approved_status: 1 };
      });
      subtask.status = subtask.status.toString() || '0';
      subtask.date = DateTimeUtility.getDateOnly(subtask.date);

      delete subtask.taskId;
      delete subtask.assignedTo;
    });
  }

  confirmSubtaskDelete(subTask: SubTaskEntity): void {
    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheetService.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translateService.instant('SubTask'),
        },
      });
      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                this.removeSubTask(subTask);
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      this._dialogService
        .open(ConfirmationDialogComponent, {
          closeButton: false,
          data: {
            type: ConfirmationDialogType.DELETE,
            emphasize: subTask.title,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (res) => {
            if (res) {
              this.removeSubTask(subTask);
            }
          },
        });
    }
  }

  removeSubTask(subTask: SubTaskEntity): void {
    const index = this.currentSubtasks.findIndex((item: any) => item.id === subTask.id);
    if (index !== -1) {
      this.currentSubtasks.splice(index, 1);
    }
    this.prepareSubtasks(this.currentSubtasks);
    this._patchSubTasks(this.currentSubtasks);
  }

  updateSubTask(subTask: SubTaskEntity): void {
    if (!isMobileView()) {
      this.bottomSheetRef = this._dialogService.open(SubtaskFormComponent, {
        closeButton: false,
        windowClass: 'dialog-overflow-unset',
        data: {
          subTask: { ...subTask, id: subTask.id },
          taskType: this.form.value.type,
          teamMembers: this.users,
          groupParticipants: this.groups.find((group) => group.id === this.form.value.groupId)?.participants,
        },
      });
    } else {
      this.bottomSheetRef = this._bottomSheetService.open(SubtaskFormComponent, {
        height: '55%',
        showCloseButton: false,
        data: {
          subTask: { ...subTask, id: subTask.id },
          taskType: this.form.value.type,
          teamMembers: this.users,
          groupParticipants: this.groups.find((group) => group.id === this.form.value.groupId)?.participants,
        },
      });
    }
    this.bottomSheetRef.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (result: any) => {
        if (result) {
          let index = -1;
          if (result.id && subTask.id) {
            index = this.currentSubtasks.findIndex((item: any) => item.id === subTask.id);
          } else {
            index = this.currentSubtasks.findIndex((item: any) => item.title === subTask.title && item.description === subTask.description);
          }

          if (index !== -1) {
            this.currentSubtasks[index] = result;
          } else {
            this.currentSubtasks.push(result);
          }
          this.prepareSubtasks(this.currentSubtasks);
          this._patchSubTasks(this.currentSubtasks);
        }
      },
    });
  }

  save(btnRef: ButtonComponent) {
    if (this.form.invalid) {
      this.markAllInvalidControlsAndScrollToFirst(this.form, this._elRef);
      return;
    }

    if (this.currentSubtasks && this.currentSubtasks.length) {
      this.currentSubtasks = this.currentSubtasks.map((subTask: any) => {
        delete subTask.assignedTo;
        if (!subTask.id) {
          delete subTask.id;
          subTask.status = subTask.status || '0';
        }
        return subTask;
      });
    }
    this.saveTask(this.currentSubtasks, btnRef);
  }

  close(): void {
    if (!isMobileView()) {
      this.dialogRef.close();
    } else {
      this._bottomSheetService.close();
    }
  }
}
